import {
  createCrew,
  createCrewMember,
  deleteCrewMember,
  getAllCrews,
  getCrewById,
  getCrewMembers,
  updateCrew,
} from '@/api/crews';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { handleAPIError } from '@/utils/errorHandler';
import { useAuth } from '@/providers/AuthProvider';

export const useCrews = () => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['crews', orgId],
    queryFn: getAllCrews,
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    crews: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useUniqueCrew = (crewId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['crew', orgId, crewId],
    queryFn: () => getCrewById(crewId),
    enabled: !!crewId && !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    crew: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useCreateCrew = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (crewData: any) => createCrew(crewData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crews', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    createCrew: mutation.mutate,
    createCrewAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useUpdateCrew = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (crewData: any) => updateCrew(crewData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crews', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    updateCrew: mutation.mutate,
    updateCrewAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

// Crew Members

export const useCrewMembers = (crewId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['crew', crewId, 'members', orgId],
    queryFn: () => getCrewMembers(crewId),
    enabled: !!crewId && !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    crewMembers: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useCreateCrewMember = (crewId: string) => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (data: any) => createCrewMember(data.userId, data.crewId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crew', crewId, 'members', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    createCrewMember: mutation.mutate,
    createCrewMemberAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useDeleteCrewMember = (crewId: string) => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (data: any) => deleteCrewMember(data.userId, data.crewId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crew', crewId, 'members', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    deleteCrewMember: mutation.mutate,
    deleteCrewMemberAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
